import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Styles
import './console.scss';

// Components
import Header from '../../components/header/header';
import Footer from '../../components/footer/footer';
import MainList from '../../components/students-lists/main-list';
import Button, { BUTTON_STYLES } from '../../components/button/button';
import GenericModal, { MODAL_TYPE } from '../../components/generic-modal/generic-modal';

// Context
import ConsoleModalContext from './context/console-modal-context';

// Actions
import consoleActions from './console-actions';
import groupActions from './actions/group-actions';
import studentSelectionActions from './actions/student-selection-actions';

// Helpers
import { getParam } from '../../helpers/utils';
import PortalCommunication from '../../helpers/portal-communications';
import Resources from '../../helpers/resources';

function Console(props) {
    // State props
    const {
        classGroupStudents,
        classGroups,
        classes,
        currentClassName,
        currentClassId,
        hasContentLoaded,
        mainCheckAllIsSelected,
        mainSelectedStudentIds,
        selectCheckboxStudent,
        selectAllCheckboxStudent,
        newCoursesAvailabilityCriteria,
        externalClassroomId,
        isSisSchool
    } = props;

    const productId = getParam('productId') || '1';
    const classId = getParam('classId') || '0';
    const countryCode = getParam('countryCode') || '';
    // Action props
    const {
        updateStudentGroup,
        updateCurrentClassId,
        getTeacherClasses,
        editClassName,
        initialiseCourseSelectOptions,
        getNewCoursesAvailabilityCriteria,
        changePassword,
        exportStudents
    } = props;

    // State
    const { i18n } = useLocalisation();
    const [hasInitialError, setHasInitialError] = useState(false);
    const [showNewCourses, setShowNewCourses] = useState(false);

    // ComponentDidMount
    useEffect(() => {
        let isCancelled = false;
        const fetchTeacherClasses = async () => {
            // Talk to the portal to hide their spinner
            PortalCommunication.requestLoadingSpinner(false);
            const response = await getTeacherClasses(productId);
            await updateCurrentClassId({ productId, classId });
            if (response.valid === false && isCancelled) {
                setHasInitialError(true);
            } else {
                // Get the config that determines for which country and year groups the New Courses will be available
                // This will be used to display the default course DDL option and enable/disable the DDL
                await getNewCoursesAvailabilityCriteria();
                /*
                    Initialise the strings for the select options
                 */
                await initialiseCourseSelectOptions({
                    countryOption: i18n._(t`Select - Default Country Option`),
                    yearOption: i18n._(t`Select - Default Year Option`),
                    allYearsOption: i18n._(t`Group Modal - Option - All grades`),
                    courseOption: i18n._(t`Select - Default Course Option`),
                    upfCourseOption: i18n._(t`Select - Default UPF Course Option`),
                    newCourseOption: i18n._(t`Select - Default Course Option`),
                    noNewCourseFoundOption: i18n._(t`Group Modal - label - Select no courses`),
                    upfCourseAdminSelectionOption: i18n._(t`Select - Use admin selection Option`),
                    upfCurrentCourseSuffix: i18n._(t`Select option - currently assigned`),
                    noUPFCourseFoundOption: i18n._(t`Group Modal - label - Select no courses`)
                });
            }
        };
        // Set defaultProductId to be 1
        fetchTeacherClasses();

        return () => {
            isCancelled = true;
        };
    }, [productId]);

    // Determine if new courses should be displayed for the teacher based on region ID and country code
    // New courses data are displayed in the main grid and in the edit/create group modals
    useEffect(() => {
        function setNewCoursesVisibility(config) {
            if (config) {
                const allowedCountryCodes = config.countryCodes || [];
                const allowedForAll = config.countryCodes.includes('ALL');
                const displayNewCourses = allowedForAll || allowedCountryCodes.includes(countryCode.toUpperCase());
                setShowNewCourses(displayNewCourses);
            }
        }
        Resources.getConfigurationItem('newCoursesVisibilityCriteria')
            .then(setNewCoursesVisibility);
    }, []);

    const closeErrorModal = useCallback(() => {
        setHasInitialError(false);
    }, [hasInitialError]);

    const [showConsoleModal, setShowConsoleModal] = useState(false);
    const [consoleModalContent, setConsoleModalContent] = useState(null);
    const [isMinimised, setIsMinimised] = useState(false);

    const [shownPassword, setShownPassword] = useState('');

    // Render
    return (
        <ConsoleModalContext.Provider
            value={{
                showConsoleModal, setShowConsoleModal, consoleModalContent, setConsoleModalContent
            }}
        >
            <Header
                classes={classes}
                productId={productId}
                currentClassName={currentClassName}
                currentClassId={currentClassId}
                updateCurrentClassId={updateCurrentClassId}
                editClassName={editClassName}
                setIsMinimised={setIsMinimised}
                classGroups={classGroups}
                setShownPassword={setShownPassword}
                studentsLength={classGroupStudents.length}
                exportStudents={exportStudents}
                showNewCourses={showNewCourses}
                newCoursesAvailabilityCriteria={newCoursesAvailabilityCriteria}
                externalClassroomId={externalClassroomId}
                isSisSchool={isSisSchool}
            />
            <main className="console">
                <div className="console__table">
                    <MainList
                        hasContentLoaded={hasContentLoaded}
                        classesLength={classes.length}
                        classGroupStudents={classGroupStudents}
                        classGroups={classGroups}
                        changePassword={changePassword}
                        mainCheckAllIsSelected={mainCheckAllIsSelected}
                        mainSelectedStudentIds={mainSelectedStudentIds}
                        selectCheckboxStudent={selectCheckboxStudent}
                        selectAllCheckboxStudent={selectAllCheckboxStudent}
                        shownPassword={shownPassword}
                        setShownPassword={setShownPassword}
                        // change group props
                        updateCurrentClassId={updateCurrentClassId}
                        selectGroupHandler={updateStudentGroup}
                        currentClassId={currentClassId}
                        productId={productId}
                        showNewCourses={showNewCourses}
                    />
                </div>
            </main>
            <Footer
                numOfClassStudents={classGroupStudents.length}
                numOfSelectedStudents={mainSelectedStudentIds.length}
                isMinimised={isMinimised}
                setIsMinimised={setIsMinimised}
                classGroupStudents={classGroupStudents}
            />
            {
                showConsoleModal === true && consoleModalContent !== null ? consoleModalContent : ''
            }
            {
                hasInitialError && (
                    <GenericModal
                        title={<Trans id="Error Message - Title" />}
                        type={MODAL_TYPE.floating}
                        closeHandler={closeErrorModal}>
                        <p className="modal__text modal__text--error">
                            <Trans id={'Error Message - Unable to proceed'} />
                        </p>
                        <div className="form__row form__row--buttons">
                            <Button
                                buttonStyle={BUTTON_STYLES.text}
                                callback={closeErrorModal}
                                visibleText={<Trans id={'Button - Ok'} />}
                            />
                        </div>
                    </GenericModal>
                )
            }
        </ConsoleModalContext.Provider>
    );
}

Console.propTypes = {
    // State
    classGroupStudents: PropTypes.array,
    classGroups: PropTypes.array,
    classes: PropTypes.array,
    currentClassName: PropTypes.string,
    currentClassId: PropTypes.number,
    hasContentLoaded: PropTypes.bool,
    mainCheckAllIsSelected: PropTypes.bool,
    mainSelectedStudentIds: PropTypes.array,
    newCoursesAvailabilityCriteria: PropTypes.object,
    // Actions
    updateStudentGroup: PropTypes.func,
    updateCurrentClassId: PropTypes.func,
    initialiseCourseSelectOptions: PropTypes.func,
    getNewCoursesAvailabilityCriteria: PropTypes.func,
    getTeacherClasses: PropTypes.func,
    editClassName: PropTypes.func,
    selectCheckboxStudent: PropTypes.func,
    selectAllCheckboxStudent: PropTypes.func,
    changePassword: PropTypes.func.isRequired,
    exportStudents: PropTypes.func,
    externalClassroomId: PropTypes.string,
    isSisSchool: PropTypes.bool
};

/*
 * mapStateToProps - set component properties from current state provided by the Reducers.
 * @param state {object} - the revised state from MessageModalCombinedReducers.
 */
function mapStateToProps(state) {
    const {
        classGroupStudents,
        classGroups,
        classes,
        currentClassName,
        currentClassId,
        hasContentLoaded,
        mainCheckAllIsSelected,
        mainSelectedStudentIds,
        newCoursesAvailabilityCriteria,
        externalClassroomId,
        isSisSchool
    } = state.consoleReducer;

    return {
        classGroupStudents,
        classGroups,
        classes,
        currentClassName,
        currentClassId,
        hasContentLoaded,
        mainCheckAllIsSelected,
        mainSelectedStudentIds,
        newCoursesAvailabilityCriteria,
        externalClassroomId,
        isSisSchool
    };
}

/*
 * mapDispatchToProps - ensure that the outputs of the Action Creators are passed to the Reducers
 * @param dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        ...consoleActions,
        updateStudentGroup: groupActions.updateStudentGroup,
        selectCheckboxStudent: studentSelectionActions.selectCheckboxStudent,
        selectAllCheckboxStudent: studentSelectionActions.selectAllCheckboxStudent
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Console);



// WEBPACK FOOTER //
// ./containers/console/console.js